import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  loginUser,
  signupUser,
  googleAuth,
  logoutUser,
  type User,
  type LoginRequest,
  type SignupRequest,
  AuthError
} from './auth';

export const CURRENT_AUTH_VERSION = 'v2';

// Auth state interface
interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  _hasHydrated: boolean;
  authVersion: string | null;

  // Actions
  login: (credentials: LoginRequest) => Promise<void>;
  signup: (userData: SignupRequest) => Promise<{ requiresVerification: boolean }>;
  loginWithGoogle: (googleToken: string) => Promise<User | null>;
  logout: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User) => void;
  setHasHydrated: (hasHydrated: boolean) => void;
}

// Create the auth store with persistence
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      _hasHydrated: false,
      authVersion: null,

      // Login action
      login: async (credentials: LoginRequest) => {
        set({ isLoading: true, error: null });

        try {
          const response = await loginUser(credentials);

          set({
            user: { ...response.user, role: response.user.role || 'user' },
            isAuthenticated: true,
            isLoading: false,
            error: null,
            authVersion: CURRENT_AUTH_VERSION,
          });
        } catch (error) {
          const errorMessage = error instanceof AuthError
            ? error.message
            : 'Login failed. Please try again.';

          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          });

          throw error; // Re-throw for component handling
        }
      },

      // Signup action
      signup: async (userData: SignupRequest) => {
        set({ isLoading: true, error: null });

        try {
          const response = await signupUser(userData);

          set({
            user: { ...response.user, role: response.user.role || 'user' },
            isAuthenticated: true,
            isLoading: false,
            error: null,
            authVersion: CURRENT_AUTH_VERSION,
          });

          return { requiresVerification: response.requiresVerification };
        } catch (error) {
          const errorMessage = error instanceof AuthError
            ? error.message
            : 'Signup failed. Please try again.';

          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          });

          throw error; // Re-throw for component handling
        }
      },      // Google Auth action (handles both signup and login)
      loginWithGoogle: async (googleToken: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await googleAuth(googleToken);

          set({
            user: { ...response.user, role: response.user.role || 'user' },
            isAuthenticated: true,
            isLoading: false,
            error: null,
            authVersion: CURRENT_AUTH_VERSION,
          });

          return response.user;
        } catch (error) {
          const errorMessage = error instanceof AuthError
            ? error.message
            : 'Google authentication failed. Please try again.';

          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          });

          throw error; // Re-throw for component handling
        }
      },

      // Logout action
      logout: async () => {
        try {
          // Call the logout API to clear httpOnly cookies
          await logoutUser();
        } catch (error) {
          // Even if the API call fails, we still clear local state
          console.error('Logout API call failed:', error);
        } finally {
          // Always clear local state
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            authVersion: null,
          });
        }
      },

      // Clear error action
      clearError: () => {
        set({ error: null });
      },

      // Set user action (for when user data is updated)
      setUser: (user: User) => {
        set({ user, isAuthenticated: true });
      },

      // Set hydration status
      setHasHydrated: (hasHydrated: boolean) => {
        set({ _hasHydrated: hasHydrated });
      },
    }),
    {
      name: 'auth-storage', // Storage key
      storage: createJSONStorage(() => localStorage), // Use localStorage
      partialize: (state) => ({
        // Only persist these fields
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        authVersion: state.authVersion,
      }),
      onRehydrateStorage: () => (state) => {
        // Wipe stale auth entries that have no role (tokens issued before RBAC was added)
        if (state?.user && !state.user.role) {
          state.user = null;
          state.isAuthenticated = false;
          state.authVersion = null;
        }
        state?.setHasHydrated(true);
      },
    }
  )
);

// Enhanced useAuth hook with hydration checking
export const useAuth = () => {
  const store = useAuthStore();

  return {
    // User data
    user: store.user,
    isAuthenticated: store.isAuthenticated && store._hasHydrated,

    // Loading states
    isLoading: store.isLoading,
    isHydrated: store._hasHydrated,

    // Error handling
    error: store.error,

    // Actions
    login: store.login,
    signup: store.signup,
    loginWithGoogle: store.loginWithGoogle,
    logout: store.logout,
    clearError: store.clearError,
    setUser: store.setUser,
  };
};

// Separate actions hook (optional, for cleaner separation)
export const useAuthActions = () => {
  const store = useAuthStore();
  return {
    login: store.login,
    signup: store.signup,
    loginWithGoogle: store.loginWithGoogle,
    logout: store.logout,
    clearError: store.clearError,
    setUser: store.setUser,
  };
};